/**
 * A conversation's files, in one place (#1069 Story 7.4, FR77, D11; re-shaped
 * by Story 8.5).
 *
 * The drawer that opens under the "Files (n)" row: every file in the
 * conversation as one full-width row -- the request's own 40pt thumbnail, the
 * file's name, its size -- with a save control beside it. The SAME piece the
 * bubble draws (`attachmentPieces.tsx`), so the two surfaces are one family.
 *
 * ⚠️ IT WAS A WRAPPING GRID OF 92pt TILES THEN A LIST OF CHIPS (Story 7.4),
 * AND STORY 8.5 MADE IT ONE LIST. The owner ruled that a message's files
 * should look like the request's Supporting Documents, "all items" -- so there
 * is no picture-first grid here any more, and no second shape. The partition
 * survives, but only as the rule for what goes in a row's box.
 *
 * ⚠️ IT IS ITS OWN COMPONENT BECAUSE `CorrespondencePanel.tsx` IS 3,400 LINES
 * AND EVERY STORY ADDS TO IT. A 150-line strip with its own suite is cheaper
 * to review and to lint, and the `.eslintrc.js` directory glob already covers
 * a new `__tests__` file here without an edit.
 *
 * ⚠️ NO CAPS, NO OVERFLOW ROW. Those are the BUBBLE's rules (`MAX_ROWS`),
 * forced by a 193px bubble. This strip is the one place a reader has asked to
 * see everything, so hiding four of nine files behind a `+5` would defeat the
 * whole feature. What bounds it is a `maxHeight` and a scroll, which the
 * reader can see the edge of.
 *
 * ⚠️ THE ORDER IS THE SERVER'S AND IS NOT RE-SORTED HERE. `findByThread`
 * returns newest message first and upload order within a message. A `.sort()`
 * in this file would be a second ordering policy that could disagree with the
 * statement's. Story 8.5 made this simpler rather than harder: there is no
 * partition applied to the drawing any more, so the rows come out in exactly
 * the order the server sent.
 *
 * ⚠️ THE STRIP IS AN INDEX, NOT A VIEWER (owner, 2026-09-19; #1069 Story 7.6,
 * D2). Every row reports THE ITEM ITSELF through one `onSelect(file)`, and the
 * panel jumps to the message it was posted with. Nothing here opens a file:
 * files open in the bubble, where FR16 always said they opened, and a
 * jump-first path keeps both (the message, and the file one tap away in it)
 * where an open-first path kept only the file. It is the item and not an index
 * because a jump needs the item's `messageId`.
 *
 * ⚠️ THE SAVE IS A SIBLING OF THE PRESS, NEVER A CHILD OF IT (#1069 Story
 * 7.5, D9, C7). Every row is a jump (above), and a save must sit BESIDE that
 * press, never as a second meaning of it. React Native has no
 * `stopPropagation`, so a save button INSIDE the row would be a nested press
 * with two meanings; and RNTL walks UP the tree for a handler, so a nested
 * press could not even be pinned honestly (`correspondence.md`, "a prop-gated
 * handler cannot be tested by firing it on the component that takes it as a
 * prop"). So each row is wrapped in a row `View` holding the UNCHANGED
 * `AttachmentRow` and a trailing `SaveFileButton`. `attachmentPieces.tsx`
 * gains nothing (7.4 D3's guard). The buttons are ALWAYS VISIBLE: the last
 * hover reveal on this panel was reversed in 7.2 because the owner could not
 * find it, and a focus-only reveal is invisible to a touch user.
 *
 * ⚠️ AND THERE IS NO CORNER BADGE ANY MORE (Story 8.5, D7). With no tile there
 * is no corner: every row gets the same 44pt trailing button a chip used to
 * get, which is one dressing instead of two and one less absolute-positioned
 * thing over a photograph.
 *
 * ⚠️ NO `Platform.OS` READ (Story 7.2's lesson). The strip has no platform
 * branch by design.
 *
 * @see src/components/correspondence/__tests__/ThreadFilesStrip.test.tsx
 */

import React from 'react';
import { ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';

import { AttachmentRow } from './attachmentPieces';
import { spokenAuthor } from './authorLabel';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { Download } from '../icons';
import { UNKNOWN_AUTHOR_TEXT } from './MessageRow';

import type { ThreadFile } from './correspondence.types';

/**
 * The strip's bound: the height of ONE PARTICULAR CONTENT SET, measured in
 * tokens (UX consult, 2026-09-18; re-derived by Story 8.5, C8). It is not a
 * general formula, and reading it as one is how it gets "corrected" into a
 * wrong number.
 *
 * The set is SIX FILES -- `MAX_FILES_PER_MESSAGE`, so the largest set one
 * message can produce:
 *
 *   ROW_HEIGHT * 6              288   six rows (40pt box + 4 padding each way)
 *   + spacing.xs * 5             20   the five gaps between them
 *   + spacing.sm * 2             16   the strip's own paddingVertical
 *                               ---
 *                               324
 *
 * ⚠️ IT WAS 300 AND IT IS 324, BECAUSE ROWS ARE TALLER THAN A TILE GRID WAS.
 * Six images used to be two rows of three 92pt tiles (184 + one gap); six rows
 * are 288. That is the cost of the owner's ruling, and it is taken knowingly:
 * the strip is the surface with room for it.
 *
 * ⚠️ THE FIVE `spacing.xs` TERMS ARE THE `content` GAP, WHICH APPLIES BETWEEN
 * EVERY CHILD. A SEVENTH file adds a row AND a sixth gap, so content taller
 * than this constant by 48 + 4 per extra file is the normal case, not a
 * defect: `maxHeight` plus the scroll is precisely what absorbs it. Do not try
 * to make the arithmetic cover n files; it is a chosen height, justified by
 * one set.
 *
 * A reader whose conversation holds about one message's worth of files
 * therefore sees all of it with no scrolling at all, which is the common case.
 * The scroll exists for the conversation with thirty, where no height is right
 * and a visible bound beats a strip that eats the composer.
 *
 * ⚠️ AND IT MUST STAY A DERIVATION. `ThreadFilesStrip.test.tsx` asserts the
 * SOURCE TEXT matches the expression and does NOT match the literal, which is
 * the reason this constant has never drifted. Pasting `324` here is a mutant
 * that suite catches.
 */
const ROW_HEIGHT = 40 + spacing.xs * 2;

export const STRIP_MAX_HEIGHT =
  ROW_HEIGHT * 6 + spacing.xs * 5 + spacing.sm * 2;

type Props = {
  files: readonly ThreadFile[];
  /**
   * The reader chose this item. The ITEM, not an index: the caller jumps to
   * `file.messageId` (Story 7.6, D2). One handler for every row -- there is
   * only one kind of row since Story 8.5, and there was only ever one meaning.
   */
  onSelect?: (file: ThreadFile) => void;
  /**
   * The reader chose to SAVE this item (Story 7.5, D9). Beside the press,
   * never a second meaning of it: the panel hands it to its own
   * `downloadAttachmentFile`. The buttons render whether or not this is
   * given -- the strip does not know whether the panel can save; the panel
   * always can.
   */
  onSave?: (file: ThreadFile) => void;
};

/**
 * What a strip item announces (Story 7.6, D10): the verb is a jump, and the
 * author is the one word that tells a screen-reader user which message they
 * are about to land on. No date -- a relative date needs `format.ts`'s Intl
 * work, and the row shows it on landing.
 */
export const stripItemLabel = (file: ThreadFile): string =>
  `Go to the message with ${file.name}, from ${
    // The bubble's own fallback for a departed user's empty name
    // (`authorNameFor`); without it the sentence ends "from " (review
    // 2026-09-19).
    // #1599 Story 4.1: the strip shows no visible author since #1069 Story
    // 8.5, so the Config Team label lives here, in the spoken form.
    spokenAuthor(file.authorName || UNKNOWN_AUTHOR_TEXT, file.authorConfigTeam)
  }`;

/**
 * The save control (D9): a 44pt button beside a row.
 *
 * ⚠️ IT HAD TWO DRESSINGS AND NOW HAS ONE (Story 8.5, D7). A tile got a 28pt
 * translucent badge in its corner with its hit area padded out to 44; with no
 * tile there is no corner, so every row gets the button. `BADGE_SIZE`,
 * `BADGE_HIT_SLOP` and the `tileBadge` style went with the tile, and with them
 * the one literal colour this file used to carry.
 */
const SaveFileButton = ({
  file,
  onSave,
  testID,
}: {
  file: ThreadFile;
  onSave?: (file: ThreadFile) => void;
  testID: string;
}) => (
  <TouchableOpacity
    testID={testID}
    style={styles.saveButton}
    onPress={() => onSave?.(file)}
    accessibilityRole="button"
    accessibilityLabel={`Save ${file.name}`}
  >
    <Download size={14} color={colors.textSecondary} />
  </TouchableOpacity>
);

export const ThreadFilesStrip = ({ files, onSelect, onSave }: Props) => (
  <View style={styles.strip} testID="correspondence-files-strip">
    {/*
      ⚠️ A NESTED VERTICAL SCROLL, AND IT IS A SIBLING OF THE MESSAGE LIST
      RATHER THAN A CHILD OF IT. 4.2's D2 rejected a scroller INSIDE a
      bubble, horizontally, where content is hidden with nothing to say so;
      this one is in the panel's own column with a visible indicator and a
      bound the reader can see the edge of. It is still the panel's second
      scroll container on native, which is a real cost and is on Story 7.6's
      walk (that is the story that scrolls the list programmatically).
    */}
    <ScrollView
      style={styles.scroll}
      nestedScrollEnabled
      showsVerticalScrollIndicator
      contentContainerStyle={styles.content}
    >
      {files.map(file => (
        // ⚠️ THE ROW IS THE SIBLING PAIR (Story 7.5, D9): the UNCHANGED
        // piece, stretched to take the width, and a trailing 44pt save
        // button BESIDE its press -- never inside it, because React Native
        // has no `stopPropagation` and a nested press would give the jump a
        // second meaning. The bubble's `maxWidth: ROW_MAX_WIDTH` is a bubble
        // rule (a long name would otherwise size the bubble); one row per
        // line is what makes this pair possible at all.
        <View key={file.id} style={styles.fileRow}>
          <AttachmentRow
            file={file}
            // ⚠️ THE STRIP'S OWN IDS, NOT THE BUBBLE'S (7.4 C9). The strip
            // shows the SAME files, so reusing `message-file-${id}` puts two
            // nodes under one testID the moment a strip is open over a
            // bubble -- and `getByTestId` throws on a duplicate, which would
            // break every Story 4.2 gallery case that presses one.
            testID={`thread-file-${file.id}`}
            style={styles.row}
            // ~240-280pt of name column at 14 (see RowProps.nameMaxChars).
            nameMaxChars={40}
            onPress={() => onSelect?.(file)}
            // NOT the bubble's `TYPE, name, size`: on this surface the press
            // is a jump, and the name has to say so (D10).
            accessibilityLabel={stripItemLabel(file)}
          />
          <SaveFileButton
            file={file}
            onSave={onSave}
            testID={`thread-file-save-${file.id}`}
          />
        </View>
      ))}
    </ScrollView>
  </View>
);

const styles = StyleSheet.create({
  strip: {
    // Muted ground so it reads as a drawer rather than more message. The side
    // padding matches the bands above it (the panel's left edge is 14, settled
    // by Story 7.1's F2).
    backgroundColor: colors.surfacePanelMuted,
    paddingHorizontal: 14,
    maxHeight: STRIP_MAX_HEIGHT,
  },
  scroll: { flexGrow: 0 },
  content: { paddingVertical: spacing.sm, gap: spacing.xs },
  fileRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  /**
   * Takes the row's width; the save button beside it takes its own 44. This is
   * the strip's half of the width policy the piece refuses to own -- the
   * bubble passes `maxWidth: ROW_MAX_WIDTH` instead.
   */
  row: { flex: 1, alignSelf: 'stretch' },
  saveButton: {
    width: spacing.minimumTapTarget,
    height: spacing.minimumTapTarget,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
